// Page Commentaires (Jalon 3.4) — l'écran de validation.
// Liste les commentaires reçus avec l'analyse de Falko + les suggestions de réponse.
// La cliente choisit/modifie une réponse et la valide.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Footer } from '../components/shared/Footer';
import {
  contestComment,
  useComments,
  validateReply,
  type CommentItem,
} from '../hooks/useComments';

const SENTIMENT = {
  POSITIVE: { label: '🙂 Positif', cls: 'bg-green-50 text-green-700' },
  NEUTRAL: { label: '😐 Neutre', cls: 'bg-neutral-100 text-neutral-600' },
  NEGATIVE: { label: '🙁 Négatif', cls: 'bg-red-50 text-red-700' },
} as const;

const SENSITIVITY = {
  MODERATE: { label: 'Délicat', cls: 'bg-amber-50 text-amber-700' },
  STRONG: { label: 'Agressif', cls: 'bg-red-50 text-red-700' },
  HATER: { label: '🚨 Hater', cls: 'bg-red-100 text-red-800' },
} as const;

const TOFU = {
  TOFU: 'Découverte',
  MOFU: 'Intéressé·e',
  BOFU: '🎯 Prêt·e à acheter',
} as const;

function Badge({ label, cls }: { label: string; cls: string }) {
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
}

function ContestBlock({ commentId }: { commentId: string }) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (sent) {
    return (
      <p className="text-xs text-green-700 mt-3 border-t border-neutral-100 pt-3">
        ✅ Merci, on examinera ça au cas par cas.
      </p>
    );
  }

  return (
    <div className="mt-3 border-t border-neutral-100 pt-3">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="text-xs text-neutral-500 hover:text-violet-700 underline"
        >
          🆘 Contester ce blocage
        </button>
      ) : (
        <div>
          <p className="text-xs text-neutral-500 mb-1">
            Explique pourquoi tu penses que c'est une erreur (avec le contexte) :
          </p>
          <textarea
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            rows={2}
            className="w-full text-sm border border-neutral-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-300 resize-y"
          />
          {err && <p className="text-xs text-red-600 mt-1">{err}</p>}
          <div className="flex gap-2 justify-end mt-2">
            <button onClick={() => setOpen(false)} className="text-xs text-neutral-500 px-3 py-1.5">
              Annuler
            </button>
            <button
              disabled={sending || !msg.trim()}
              onClick={async () => {
                setSending(true);
                setErr(null);
                try {
                  await contestComment(commentId, msg);
                  setSent(true);
                } catch (e) {
                  setErr(e instanceof Error ? e.message : 'Erreur');
                } finally {
                  setSending(false);
                }
              }}
              className="text-xs bg-neutral-800 hover:bg-neutral-900 disabled:opacity-40 text-white px-3 py-1.5 rounded-lg"
            >
              {sending ? 'Envoi…' : 'Envoyer'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CommentRow({ comment, onValidated }: { comment: CommentItem; onValidated: () => void }) {
  const gen = comment.aiSuggestions;
  const [text, setText] = useState(gen?.suggestions?.[0] ?? '');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [published, setPublished] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onValidate = async () => {
    setSaving(true);
    setErr(null);
    try {
      const res = await validateReply(comment.id, text);
      setPublished(res.published);
      setDone(true);
      onValidated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-5">
      {/* En-tête */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-neutral-900">@{comment.authorHandle}</span>
        <span className="text-xs text-neutral-400">
          {new Date(comment.createdAt).toLocaleDateString('fr-FR')}
        </span>
      </div>

      {/* Commentaire reçu */}
      <p className="text-neutral-800 mb-3 italic">« {comment.text} »</p>

      {/* Analyse */}
      <div className="flex flex-wrap gap-2 mb-4">
        {comment.sentiment && SENTIMENT[comment.sentiment] && (
          <Badge label={SENTIMENT[comment.sentiment].label} cls={SENTIMENT[comment.sentiment].cls} />
        )}
        {comment.sensitivityLevel && comment.sensitivityLevel !== 'NONE' && SENSITIVITY[comment.sensitivityLevel] && (
          <Badge
            label={SENSITIVITY[comment.sensitivityLevel].label}
            cls={SENSITIVITY[comment.sensitivityLevel].cls}
          />
        )}
        {comment.tofuMofuBofu && (
          <Badge label={TOFU[comment.tofuMofuBofu]} cls="bg-violet-50 text-violet-700" />
        )}
      </div>

      {done ? (
        <div className="bg-green-50 text-green-700 text-sm rounded-lg px-4 py-3">
          {published
            ? '✅ Réponse publiée sur Instagram !'
            : '✅ Réponse validée (elle sera publiée dès que ton compte Instagram est connecté).'}
        </div>
      ) : !gen ? (
        <div className="text-sm text-neutral-400">⏳ Analyse en cours…</div>
      ) : gen.action === 'REFUSE' ? (
        <div className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-3">
          🚫 Falko n'a pas généré de réponse (garde-fou). {gen.reason}
        </div>
      ) : (
        <div>
          {gen.action === 'FLAG' && (
            <div className="bg-amber-50 text-amber-800 text-sm rounded-lg px-4 py-3 mb-3">
              ⚠️ À vérifier avant de répondre. {gen.reason}
            </div>
          )}

          {/* Suggestions cliquables */}
          <p className="text-xs font-medium text-neutral-500 mb-2">Suggestions de Falko :</p>
          <div className="space-y-2 mb-3">
            {gen.suggestions.map((s, i) => (
              <button
                key={i}
                onClick={() => setText(s)}
                className={`block w-full text-left text-sm px-3 py-2 rounded-lg border transition ${
                  text === s
                    ? 'border-violet-400 bg-violet-50 text-violet-900'
                    : 'border-neutral-200 hover:border-violet-300 hover:bg-violet-50/50 text-neutral-700'
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          {/* Édition + validation */}
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            className="w-full text-sm border border-neutral-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-300 resize-y"
            placeholder="Choisis une suggestion ci-dessus, ou écris ta réponse…"
          />
          {err && <p className="text-xs text-red-600 mt-1">{err}</p>}
          <div className="flex justify-end mt-3">
            <button
              onClick={onValidate}
              disabled={saving || !text.trim()}
              className="bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
            >
              {saving ? 'Validation…' : 'Valider la réponse'}
            </button>
          </div>
        </div>
      )}

      {gen && (gen.action === 'FLAG' || gen.action === 'REFUSE') && !done && (
        <ContestBlock commentId={comment.id} />
      )}
    </div>
  );
}

export const Comments = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { comments, loading, error, reload } = useComments('PENDING');

  const onLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-semibold text-neutral-900">Falko</h1>
            <Link to="/dashboard" className="text-sm text-neutral-500 hover:text-neutral-800">
              ← Tableau de bord
            </Link>
          </div>
          <button
            onClick={onLogout}
            className="text-sm text-neutral-600 hover:text-neutral-900 px-3 py-1.5 rounded-lg hover:bg-neutral-100 transition"
          >
            Se déconnecter
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <h2 className="text-2xl font-semibold text-neutral-900 mb-1">Commentaires à traiter</h2>
        <p className="text-neutral-500 text-sm mb-6">
          Falko a analysé chaque commentaire et te propose des réponses dans ta voix. À toi de valider.
        </p>

        {loading ? (
          <p className="text-neutral-400 text-sm">Chargement…</p>
        ) : error ? (
          <p className="text-red-600 text-sm">{error}</p>
        ) : comments.length === 0 ? (
          <div className="bg-white rounded-2xl border border-neutral-200 p-8 text-center">
            <span className="text-3xl">🎉</span>
            <p className="text-neutral-600 mt-2">Aucun commentaire en attente. Tout est traité !</p>
          </div>
        ) : (
          <div className="space-y-4">
            {comments.map((c) => (
              <CommentRow key={c.id} comment={c} onValidated={() => void reload()} />
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};
